import { normalizzaCodiceFiscale, validaCodiceFiscale } from '@aulaviva/shared';
import { forwardRef, useState, type ChangeEvent, type FocusEvent } from 'react';
import { TextField, type TextFieldProps } from './TextField.js';

export interface CodiceFiscaleFieldProps extends Omit<TextFieldProps, 'mono' | 'maxLength' | 'onChange' | 'value' | 'defaultValue' | 'label'> {
  label?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (valore: string, evento: ChangeEvent<HTMLInputElement>) => void;
  /** Se true, al blur valida con le regole del brand book e mostra il messaggio. Default true. */
  validaAlBlur?: boolean;
}

/**
 * Campo del codice fiscale: font mono, maiuscolo mentre si scrive, contatore "N di 16",
 * messaggi di errore nel tono del brand book.
 */
export const CodiceFiscaleField = forwardRef<HTMLInputElement, CodiceFiscaleFieldProps>(function CodiceFiscaleField(
  { value, defaultValue, onChange, onBlur, error, validaAlBlur = true, label = 'Codice fiscale', ...rest },
  ref,
) {
  const [interno, setInterno] = useState(defaultValue ?? '');
  const [erroreLocale, setErroreLocale] = useState<string | undefined>();
  const controllato = value !== undefined;
  const corrente = controllato ? value : interno;

  function gestisciChange(e: ChangeEvent<HTMLInputElement>) {
    const normalizzato = normalizzaCodiceFiscale(e.target.value).slice(0, 16);
    e.target.value = normalizzato;
    if (!controllato) setInterno(normalizzato);
    if (erroreLocale) setErroreLocale(undefined);
    onChange?.(normalizzato, e);
  }

  function gestisciBlur(e: FocusEvent<HTMLInputElement>) {
    if (validaAlBlur && corrente.length > 0) {
      const esito = validaCodiceFiscale(corrente);
      setErroreLocale(esito.valido ? undefined : esito.messaggio);
    }
    onBlur?.(e);
  }

  return (
    <TextField
      ref={ref}
      label={label}
      mono
      value={corrente}
      onChange={gestisciChange}
      onBlur={gestisciBlur}
      error={error ?? erroreLocale}
      maxLength={16}
      autoCapitalize="characters"
      autoCorrect="off"
      spellCheck={false}
      inputMode="text"
      meta={<span aria-live="polite">{corrente.length} di 16</span>}
      {...rest}
    />
  );
});
